import { useEffect } from 'react';
import Favicon from 'react-favicon';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import { Route, Routes, useLocation, useNavigate } from 'react-router';
import { Navigate } from 'react-router-dom';
import queryString from 'query-string';
import { WithStyles, withStyles } from '@mui/styles';
import { LDFlagSet } from 'launchdarkly-react-client-sdk';

// Contexts
import { withTenant, withTranslate } from '../core/TenantProvider';
// Components - Atoms, Organisms,Containers
import APLayout from '../components/organisms/APLayout';
import APBackdropLoader from '../components/atoms/APBackdropLoader';
import APGoogleHeaderScript from '../components/organisms/APGoogleHeaderScript';
import APGoogleBodyScript from '../components/organisms/APGoogleBodyScript';
import AccountContainer from '../containers/AccountContainer';
import CommissionsContainer from '../containers/CommissionsContainer';
import ErrorContainer from './../containers/ErrorContainer';
import LoginContainer from '../containers/LoginContainer';
import KeycloakLoginContainer from '../containers/KeycloakLoginContainer';
import TokenValidationContainer from '../containers/TokenValidationContainer';
import ReferContainer from '../containers/ReferContainer';
import ReferralsContainer from '../containers/ReferralsContainer';
import RegistrationContainer from '../containers/RegistrationContainer';
import NotificationsContainer from '../containers/NotificationsContainer';
import ResourcesContainer from '../containers/ResourcesContainer';
import { SettingsContainer } from '../containers/SettingsContainer';
import AddBankDetailsContainer from '../containers/AddBankDetailsContainer';
import MaintenanceContainer from './../containers/MaintenanceContainer';
import InviteAffiliateContainer from './../containers/InviteAffiliateContainer';
import ResetPasswordContainer from '../containers/PasswordReset/ResetPasswordContainer';
import InvoicesContainer from '../containers/InvoicesContainer';
import CreatePasswordContainer from '../containers/PasswordReset/CreatePasswordContainer';
import ClientManagementContainer from '../containers/ClientManagementContainer';
import BecomePartnerContainer from '../containers/BecomePartnerContainer';
import NewBecomePartnerContainer from '../containers/NewBecomePartnerContainer';
// Files in the same folder
import PrivateRoute from './PrivateRoute';
// import history from './history';
import MainStyles from './styles/index.style';
// Types
import { RoleAccessFunction } from '../core/types/AffiliateDetails';
// Redux
import { actions as UserActions } from '../redux/modules/user';
import { ReducerState } from './../redux/modules';
import { RemoveAllAuthCookie } from '../redux/modules/user/actions';
// Utils
import { addUserContext, initCoraLogix } from '../core/utils/Coralogix';
// Constants
import { APPROVE_PARTNER, THIRD_PARTY_AUTHENTICATION_ROUTE } from '../core/utils/Constants/Constants';


interface MainProps extends WithStyles<typeof MainStyles> {
    isAuthenticated: boolean | null,
    authLoadingState: string,
    settings: any,
    translate: Function,
    validate: Function,
    affiliateDetails: any,
    affiliateLoadingState: string,
    resetUserDetailsStates: Function,
    roleAccessFunction: RoleAccessFunction,
    launchDarklyFlags: LDFlagSet,
    forTestingCall?: boolean,
}

const Main = (props: MainProps) => {
    const {
        isAuthenticated,
        validate,
        resetUserDetailsStates,
        settings,
        translate,
        affiliateDetails,
        roleAccessFunction,
        launchDarklyFlags,
        authLoadingState,
        forTestingCall = false
    } = props;

    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        window.location.hostname !== 'localhost' && initCoraLogix();

        if ((isAuthenticated === null || isAuthenticated === false) && authLoadingState !== 'UNINITIALISED') {
            RemoveAllAuthCookie();
            resetUserDetailsStates();
        }

        if (isAuthenticated) {
            !forTestingCall && validate(navigate, location);
        }
    }, []);

    useEffect(() => {
        if (isAuthenticated && affiliateDetails !== null && affiliateDetails?.affiliate) {
            const { user_emailid, affiliate_Number } = affiliateDetails.affiliate || {};

            addUserContext(user_emailid, affiliate_Number);
        }
    }, [affiliateDetails, isAuthenticated]);

    useEffect(() => {
        document.title = translate("document.title");
    }, [translate]);

    const {
        VIEW_ACCOUNT,
        VIEW_CLIENT_MANAGEMENT,
        REGISTER_CLIENT,
        VIEW_INVOICES,
        VIEW_COMMISSION,
        REFER_CLIENT,
        VIEW_REFERRALS,
        VIEW_NOTIFICATIONS,
        VIEW_RESOURCES,
        VIEW_SETTINGS
    } = roleAccessFunction;

    const { enableRegisterClientFlow, singlePageBapFlow, bapFlowAccess, passwordResetFlowAccess, approvepartnerFlowAccess } = launchDarklyFlags || {};

    const shouldEnableRAC: boolean = enableRegisterClientFlow ?? true;
    const shouldEnableBAP: boolean = bapFlowAccess ?? true;
    const shouldEnableCreatePassword: boolean = passwordResetFlowAccess ?? true;
    const shouldEnableApprovePartner: boolean = approvepartnerFlowAccess ?? true;

    const friptaAccountLoginDefaultRoute: string = VIEW_ACCOUNT ? '/account' : '/client_management';

    // Check for token query parameter
    const queryParam: queryString.ParsedQuery<string> = queryString.parse(location.search);
    const hasToken = Boolean(queryParam.token);

    return (
        authLoadingState === 'LOADING' ? <APBackdropLoader open={true} /> :
            <>
                <APGoogleHeaderScript />
                <APGoogleBodyScript />
                <Favicon url={settings.FAVICON} />
                <Routes>
                    <Route path="/" element={hasToken ? (<APLayout><TokenValidationContainer /></APLayout>) : (<Navigate to={`/login`} replace={true} />)} />
                    <Route path="/login" element={isAuthenticated ? (<Navigate to={friptaAccountLoginDefaultRoute} replace />) : (<APLayout><LoginContainer /></APLayout>)} />
                    <Route path="/keycloak_auth_fail" element={<APLayout><ErrorContainer /></APLayout>} />
                    <Route path={THIRD_PARTY_AUTHENTICATION_ROUTE} element={<APLayout><KeycloakLoginContainer /></APLayout>} />
                    {!isAuthenticated && <Route path="/resetpassword" element={<APLayout><ResetPasswordContainer /></APLayout>} />}
                    <Route path="/createpassword" element={<APLayout> {shouldEnableCreatePassword ? <CreatePasswordContainer /> : <MaintenanceContainer />}</APLayout>} />
                    {!isAuthenticated && shouldEnableBAP && <Route path="/register-testing" element={singlePageBapFlow ? <APLayout><NewBecomePartnerContainer /></APLayout> : <APLayout><BecomePartnerContainer /></APLayout>} />}
                    {!isAuthenticated && shouldEnableBAP && <Route path="/register" element={<APLayout><NewBecomePartnerContainer /></APLayout>} />}
                    <Route path="/maintenance" element={<APLayout><MaintenanceContainer /></APLayout>} />
                    <Route path={APPROVE_PARTNER} element={<APLayout> {shouldEnableApprovePartner ? <InviteAffiliateContainer /> : <MaintenanceContainer />}</APLayout>} />
                    <Route element={<PrivateRoute />}>
                        {<Route path="/account" element={<APLayout>{VIEW_ACCOUNT && <AccountContainer />}</APLayout>} />}
                        {<Route path="/invoices" element={<APLayout>{VIEW_INVOICES && <InvoicesContainer />}</APLayout>} />}
                        {<Route path="/commissions" element={<APLayout>{VIEW_COMMISSION && <CommissionsContainer />}</APLayout>} />}
                        {<Route path="/refer" element={<APLayout>{REFER_CLIENT && <ReferContainer />}</APLayout>} />}
                        {<Route path="/client_management" element={<APLayout>{VIEW_CLIENT_MANAGEMENT && <ClientManagementContainer />}</APLayout>} />}
                        {<Route path="/referrals" element={<APLayout>{VIEW_REFERRALS && <ReferralsContainer />}</APLayout>} />}
                        {(REGISTER_CLIENT && VIEW_CLIENT_MANAGEMENT) && shouldEnableRAC && <Route path="/register_client" element={<APLayout><RegistrationContainer /></APLayout>} />}
                        {<Route path="/notifications" element={<APLayout>{VIEW_NOTIFICATIONS && <NotificationsContainer />}</APLayout>} />}
                        {<Route path="/resources" element={<APLayout>{VIEW_RESOURCES && <ResourcesContainer />}</APLayout>} />}
                        {<Route path="/settings" element={<APLayout>{VIEW_SETTINGS && <SettingsContainer />}</APLayout>} />}
                        {<Route path="/addBankDetails" element={<APLayout>{VIEW_SETTINGS && <AddBankDetailsContainer />}</APLayout>} />}
                        <Route path="*" element={<APLayout><ErrorContainer /></APLayout>} />
                    </Route>
                </Routes>
            </>
    );
};

const mapStateToProps = (store: ReducerState) => ({
    isAuthenticated: store.user.isAuthenticated,
    authLoadingState: store.user.loadingState,
    affiliateDetails: store.user.affiliateDetails,
    affiliateLoadingState: store.user.affiliateLoadingState,
    roleAccessFunction: store.user.roleAccessFunction,
    launchDarklyFlags: store.user.launchDarklyFlags
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({
    validate: UserActions.validate,
    resetUserDetailsStates: UserActions.resetUserDetailsStates
}, dispatch);

export default withTenant(
    withTranslate(
        connect(mapStateToProps, mapDispatchToProps)(
            withStyles(MainStyles)(Main)
        )
    )
);